import React, { useRef } from "react";
import styled from "styled-components";
import type { RaceClock } from "../../hooks/useRaceClock";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin: 4px 0 18px;
  color: #323232;
`;

const TapButton = styled.button<{ $armed: boolean }>`
  margin-top: 12px;
  min-width: 180px;
  padding: 14px 28px;
  font-size: 1.15rem;
  font-weight: 900;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: white;
  background: ${(p) => (p.$armed ? "#009f6f" : "#b9b9b9")};
  border: none;
  border-radius: 4px;
  cursor: ${(p) => (p.$armed ? "pointer" : "default")};
  /* no double-tap zoom wait on phones */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
`;

const Note = styled.div`
  font-size: 0.75rem;
  font-weight: 400;
  color: #888;
  margin-top: 8px;
  max-width: 320px;
`;

/** `1.30s` / `450ms`. */
function formatOffset(ms: number): string {
  const abs = Math.abs(ms);
  return abs >= 1000 ? `${(abs / 1000).toFixed(2)}s` : `${Math.round(abs)}ms`;
}

// Below this a device clock is close enough not to mention.
const CLOCK_NOTE_MIN_MS = 300;

export interface RaceButtonProps {
  clock: RaceClock;
  /** Only signed-in members get the button; for visitors this renders nothing. */
  canRace: boolean;
  /** A race request is on its way. */
  busy?: boolean;
  /** Fires the race tap with `performance.now()` from when the finger landed. */
  onTap: (tapStartedAt: number) => void;
  /** Label once armed (default "tap"). */
  armedLabel?: string;
}

/**
 * The race button. It stays grey ("wait") until unlock by the SERVER clock
 * and turns green at that moment, so a phone whose clock runs fast or slow
 * still races on time. No countdown is shown (owner's call, 2026-11-03).
 * It fires on pointer-down (the moment the finger lands, not when it lifts)
 * and on keyboard activation; the click that ends the same press is not sent
 * again.
 */
export const RaceButton: React.FC<RaceButtonProps> = ({
  clock,
  canRace,
  busy = false,
  onTap,
  armedLabel = "tap",
}) => {
  // When a pointer-down last fired, so the click the same press ends with is
  // dropped (null until the first one).
  const pointerFiredAtRef = useRef<number | null>(null);
  const armed = (clock.msToUnlock ?? 0) <= 0;

  if (!canRace) return null;

  const fire = (startedAt: number): boolean => {
    if (!armed || busy) return false;
    onTap(startedAt);
    return true;
  };

  const ahead = clock.clockAheadMs;
  const showClockNote = !armed && typeof ahead === "number" && Math.abs(ahead) >= CLOCK_NOTE_MIN_MS;

  return (
    <Wrap>
      <TapButton
        type="button"
        $armed={armed}
        disabled={!armed || busy}
        aria-disabled={!armed || busy}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          const now = performance.now();
          if (fire(now)) pointerFiredAtRef.current = now;
        }}
        onClick={() => {
          const now = performance.now();
          const pointerAt = pointerFiredAtRef.current;
          pointerFiredAtRef.current = null;
          if (pointerAt !== null && now - pointerAt < 1000) return; // same press
          fire(now);
        }}
      >
        {armed ? armedLabel : "wait"}
      </TapButton>
      {showClockNote && (
        <Note>
          Your device clock is {formatOffset(ahead!)} {ahead! > 0 ? "ahead" : "behind"}. Go by
          the button, not your phone&apos;s clock: it turns green at unlock.
        </Note>
      )}
    </Wrap>
  );
};

export default RaceButton;
